/**
 * IncompleteBanner — Phase E1 level-2 view.
 *
 * Renders on each detail page (Agreement / Person / Account / Policy) when the
 * record fails one or more active completeness rules. Lists exactly which
 * fields are missing, with the rule's human description as a tooltip.
 *
 * Pedro 2026-10-10: each field shows by its readable name (§3j — it showed the raw
 * column name before) and, given `onOpenTab`, is clickable to open the tab that
 * holds it (MissingFields.FIELD_TABS). The tabs and the inputs carry the same
 * violet marks (MissingFields).
 *
 * Renders nothing when the record is complete or while loading.
 */
import { Alert, Badge, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconClipboardCheck } from '@tabler/icons-react';

import { fieldShortLabel } from '../api/fieldRegistry';
import type { CompletenessEntityType, UUID } from '../api/types';
import { useFieldsMissing } from '../hooks/useCompleteness';
import { FIELD_TABS } from './MissingFields';

interface Props {
  entity_type: CompletenessEntityType;
  id: UUID | undefined;
  /** Opens a tab of the page; makes each field clickable. */
  onOpenTab?: (tab: string) => void;
}

export function IncompleteBanner({ entity_type, id, onOpenTab }: Props) {
  const q = useFieldsMissing(entity_type, id);
  if (!id || q.isLoading || !q.data) return null;
  if (q.data.missing_fields.length === 0) return null;

  // #40/old-#378 (2026-06-11): the banner was ORANGE — the same colour
  // the app uses for "staged, pending Save" — and Pedro mis-read it as
  // unsaved changes during the 2026-06-08 smoke. Violet + clipboard icon
  // + "Compliance" in the title anchor it as a data-quality flag from
  // the completeness rules, not an editing state. (Colour vocabulary:
  // orange = staged, blue = filed/pending approval, green = applied,
  // red = error/deleted, violet = compliance data-quality.)
  return (
    <Alert color="violet" variant="light" icon={<IconClipboardCheck size={16} />}
      title={`Compliance — incomplete record: ${q.data.missing_fields.length} required field${q.data.missing_fields.length === 1 ? '' : 's'} missing`}>
      <Stack gap={6}>
        <Text size="sm">
          The completeness rules flag the fields below as required. This is a
          data-quality notice — it does not mean you have unsaved changes.
          {onOpenTab && ' Click a field to open its tab; it is outlined in violet there.'}
        </Text>
        <Group gap={4} wrap="wrap">
          {q.data.missing_fields.map((f, i) => {
            const tab = FIELD_TABS[entity_type][f];
            const label = fieldShortLabel(entity_type, f);
            return (
              <Tooltip key={f} label={q.data!.explanations[i]} withArrow openDelay={200}>
                {onOpenTab && tab ? (
                  // type="button": never a form submit, wherever the banner sits (C-113).
                  <Badge component="button" type="button" variant="light" color="violet" size="sm"
                    style={{ cursor: 'pointer', border: 'none' }} onClick={() => onOpenTab(tab)}>
                    {label}
                  </Badge>
                ) : (
                  <Badge variant="light" color="violet" size="sm">{label}</Badge>
                )}
              </Tooltip>
            );
          })}
        </Group>
      </Stack>
    </Alert>
  );
}
